import { useSelector } from 'react-redux';
import InstagramIcon from '../../assets/logo/InstagramIcon';
import useMaps from '../../hooks/location/useMaps';
import { Link } from 'react-router-dom';
import { SCREENS } from '../../services/exports/Constants';
import { useTranslation } from 'react-i18next';
import { StoreInterface } from '../../store/types';
import { Stringable } from '../../services/helpers/Stringable';
import ImpressumResource from '../../services/resources/ImpressumResource';
import { openCookieSettings } from '../../services/helpers/TrackingConsent';

export default function Footer() {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Footers:Footer' });

  const { company } = useSelector((state: StoreInterface) => state.initialData);

  const { openMap } = useMaps();

  // A self-serve restaurant links only the imprint it pasted, and none until it has.
  const usesOwnImpressum = ImpressumResource.usesOwnImpressum(company);

  function getImpressum() {
    const isDemo = company?.id === 21 || company?.id === 8;

    return {
      show: isDemo || company?.impressum_link,
      href: isDemo ? SCREENS.IMPRESSUM : company?.impressum_link,
    };
  }

  function renderInfo() {
    return (
      <div className={infoContainer}>
        <div className={infoItemContainer}>
          <h6 className={infoTitleStyle}>{company?.name}</h6>
          {company?.instagram_link && (
            <a href={company?.instagram_link} target={'_blank'} rel="noreferrer">
              <InstagramIcon className={socialIconStyle} />
            </a>
          )}
        </div>
        <div className={infoItemContainer}>
          <h6 className={infoTitleStyle}>{t('address')}</h6>
          <div className="cursor-pointer" onClick={openMap}>
            <p className={linkStyle}>{company?.name}</p>
            <p className={linkStyle}>{company?.address}</p>
            {company?.city && (
              <p className={linkStyle}>
                {company?.zip_code && `${company?.zip_code} `}
                {company?.city}
              </p>
            )}
          </div>
        </div>
        <div className={infoItemContainer}>
          <h6 className={infoTitleStyle}>{t('contact')}</h6>
          {company?.phone_number && (
            <a href={`tel:${company?.phone_number}`} className={linkStyle}>
              <p className="text-xs">{company?.phone_number}</p>
            </a>
          )}
          {company?.email && (
            <a href={`mailto:${company?.email}`} className={linkStyle}>
              <p className="text-xs">{company?.email}</p>
            </a>
          )}
          {company?.website_link && (
            <a
              href={new Stringable(company?.website_link).start('https://').toString()}
              target="_blank"
              rel="noopener noreferrer"
              className={linkStyle}
            >
              <p className="text-xs">{company?.website_link}</p>
            </a>
          )}
        </div>
      </div>
    );
  }

  function renderBase() {
    return (
      <div className={baseContainer}>
        <div className={baseWrapper}>
          <Link
            to={SCREENS.TERMS_AND_CONDITIONS}
            className={baseItemContainer}
            target="_blank"
            rel="noopener noreferrer"
          >
            <p className="text-xs">{t('terms')}</p>
          </Link>
          <Link to={SCREENS.PRIVACY_POLICY} className={baseItemContainer} target="_blank" rel="noopener noreferrer">
            <p className="text-xs">{t('privacy_policy')}</p>
          </Link>
          {!usesOwnImpressum && getImpressum().show && (
            <a className={baseItemContainer} href={getImpressum().href} target={'_blank'} rel="noreferrer">
              <p className="text-xs">{t('impressum')}</p>
            </a>
          )}
          <Link to={SCREENS.COOKIE_POLICY} className={baseItemContainer} target="_blank" rel="noopener noreferrer">
            <p className="text-xs">{t('cookie_policy')}</p>
          </Link>
          {/* GDPR Art. 7(3): consent must be as easy to withdraw as to give. */}
          <button
            type="button"
            data-test="cookie-settings-button"
            onClick={openCookieSettings}
            className={baseItemContainer}
          >
            <p className="text-xs">{t('cookie_settings')}</p>
          </button>
          {(!usesOwnImpressum || ImpressumResource.hasOwnImpressum(company)) && (
            <Link to={SCREENS.IMPRESSUM} className={baseItemContainer} rel="noopener noreferrer">
              <p className="text-xs">{t('impressum')}</p>
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={container}>
      {renderInfo()}
      {renderBase()}
    </div>
  );
}

const container =
  'bottom-0 w-full lg:p-lg p-none lg:pb-none bg-gray-600 dark:bg-brand-card lg:rounded-2xl rounded-none';

const infoContainer = 'grid lg:grid-cols-3 grid-cols-1 gap-4 lg:py-md py-md lg:px-lg px-md';

const infoItemContainer = '';

const infoTitleStyle = 'mb-md text-brand-text-primary';

const baseContainer = 'flex flex-row justify-between lg:items-center items-start py-md lg:px-lg px-md';

const baseWrapper = 'flex-1 flex lg:flex-row flex-col lg:items-center items-start justify-between mr-xl';

const baseItemContainer =
  'flex mr-lg items-center lg:py-none pb-xs hover:underline decoration-black decoration-solid underline-offset-4 line-clamp-1';

const socialIconStyle = 'h-10 w-10';

const linkStyle = 'text-xs text-left mb-xs hover:underline text-brand-text-primary';
